/**
 * ApprovalMatrixSettings — admin-only UI to configure value tiers.
 *
 * The matrix is what /api/approval-matrix returns. Edits are sent
 * back via PUT — the server validates contiguity and rejects gaps /
 * overlaps with a 400 + message that we surface as a toast.
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Save, Plus, Trash2, ShieldAlert } from 'lucide-react';

const ROLE_OPTIONS = [
  { value: '',            label: 'Auto-approved (no approver)' },
  { value: 'accountant',  label: 'Accountant' },
  { value: 'admin',       label: 'Admin / CFO' },
  { value: 'manager',     label: 'Manager' },
  { value: 'sales_rep',   label: 'Sales Rep' },
];

export default function ApprovalMatrixSettings({ user }) {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get('/approval-matrix');
        setTiers(data.tiers || []);
      } catch (_) {
        toast.error('Failed to load matrix');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updateTier = (i, patch) => {
    setTiers((ts) => ts.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  };

  const addTier = () => {
    // New tier inserts just before the open-ended last tier (max=null).
    const lastIdx = tiers.length - 1;
    const lastMax = tiers[lastIdx]?.min ?? 0;
    setTiers((ts) => [
      ...ts.slice(0, lastIdx),
      { min: lastMax, max: lastMax + 100000, approver_role: 'admin', label: 'New tier' },
      ...ts.slice(lastIdx).map((t, k) => k === 0 ? { ...t, min: lastMax + 100000 } : t),
    ]);
  };

  const removeTier = (i) => {
    if (tiers.length <= 2) {
      toast.error('Need at least 2 tiers');
      return;
    }
    // Bridge the gap so the matrix stays contiguous.
    setTiers((ts) => {
      const out = [...ts];
      const removed = out.splice(i, 1)[0];
      // Snap the next tier's min down to the removed tier's min.
      if (i < out.length) out[i] = { ...out[i], min: removed.min };
      return out;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        tiers: tiers.map((t) => ({
          min: Number(t.min) || 0,
          max: t.max === null || t.max === '' || t.max === undefined ? null : Number(t.max),
          approver_role: t.approver_role || null,
          label: t.label || '',
        })),
      };
      const { data } = await axiosInstance.put('/approval-matrix', payload);
      toast.success(`Matrix saved · ${data.tiers.length} tiers`);
      setTiers(data.tiers);
    } catch (err) {
      const det = err.response?.data?.detail;
      toast.error(typeof det === 'object' ? det.message || det.error : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading…</div>;

  return (
    <div data-testid="approval-matrix-settings-page" className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Approval Matrix</h2>
          <p className="text-sm text-gray-500">
            Value-based gating for Quotation → Invoice conversion.
            Tiers must be contiguous (each tier&apos;s max = next tier&apos;s min).
          </p>
        </div>
        {isAdmin && (
          <button
            data-testid="save-matrix-button"
            onClick={save}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-md inline-flex items-center gap-1.5"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save Matrix'}
          </button>
        )}
      </div>

      {!isAdmin && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-3 text-sm flex items-center gap-2">
          <ShieldAlert className="w-4 h-4" />
          Read-only view — only admins can edit the matrix.
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-600">
            <tr>
              <th className="text-right py-2 px-3">Min (SAR)</th>
              <th className="text-right py-2 px-3">Max (SAR)</th>
              <th className="text-left py-2 px-3">Approver Role</th>
              <th className="text-left py-2 px-3">Label</th>
              {isAdmin && <th className="py-2 px-3 w-12"></th>}
            </tr>
          </thead>
          <tbody className="divide-y">
            {tiers.map((t, i) => (
              <tr key={i} data-testid={`tier-row-${i}`}>
                <td className="py-2 px-3">
                  <input
                    type="number"
                    disabled={!isAdmin}
                    value={t.min ?? 0}
                    onChange={(e) => updateTier(i, { min: e.target.value })}
                    className="w-32 text-right form-input text-sm"
                    data-testid={`tier-min-${i}`}
                  />
                </td>
                <td className="py-2 px-3">
                  <input
                    type="number"
                    disabled={!isAdmin || i === tiers.length - 1}
                    value={t.max ?? ''}
                    placeholder={i === tiers.length - 1 ? '∞' : ''}
                    onChange={(e) => updateTier(i, { max: e.target.value })}
                    className="w-32 text-right form-input text-sm"
                    data-testid={`tier-max-${i}`}
                  />
                </td>
                <td className="py-2 px-3">
                  <select
                    disabled={!isAdmin}
                    value={t.approver_role || ''}
                    onChange={(e) => updateTier(i, { approver_role: e.target.value || null })}
                    className="form-input text-sm w-full max-w-xs"
                    data-testid={`tier-role-${i}`}
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </td>
                <td className="py-2 px-3">
                  <input
                    type="text"
                    disabled={!isAdmin}
                    value={t.label || ''}
                    onChange={(e) => updateTier(i, { label: e.target.value })}
                    className="form-input text-sm w-full"
                    data-testid={`tier-label-${i}`}
                  />
                </td>
                {isAdmin && (
                  <td className="py-2 px-3 text-right">
                    <button
                      data-testid={`tier-remove-${i}`}
                      onClick={() => removeTier(i)}
                      className="text-red-600 hover:text-red-800"
                      title="Remove tier"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {isAdmin && (
          <div className="p-3 border-t bg-gray-50">
            <button
              data-testid="add-tier-button"
              onClick={addTier}
              className="text-sm text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> Add tier
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
